/**
 * TerminationSimulatorDialog — Simulateur "Si je résilie aujourd'hui ?"
 * Calcule l'indemnité de préavis + coût restant + économie potentielle.
 */
import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import { Label } from "@/components/ui/label";
import {
  Calculator, Loader2, Calendar, TrendingDown, TrendingUp, AlertTriangle,
  CheckCircle2, Ban, Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const fmtEUR = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR",
  { day: "2-digit", month: "long", year: "numeric" }) : "—";

const RECO_META = {
  recommandé: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
                Icon: CheckCircle2, label: "Résiliation recommandée" },
  "à_évaluer": { cls: "bg-amber-50 text-amber-800 border-amber-200",
                  Icon: Info, label: "À évaluer" },
  non_rentable: { cls: "bg-red-50 text-red-700 border-red-200",
                    Icon: Ban, label: "Peu rentable" },
};

export default function TerminationSimulatorDialog({ open, onOpenChange, contract }) {
  const [cancelDate, setCancelDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(null);

  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().slice(0, 10);
      setCancelDate(today);
      setSim(null);
      // Auto-run pour aujourd'hui
      simulate(today);
    }
    // eslint-disable-next-line
  }, [open]);

  const simulate = async (date) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/simulate-termination`, {
        cancellation_date: date,
      });
      setSim(data.simulation);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const reco = sim && RECO_META[sim.recommendation]
    ? RECO_META[sim.recommendation]
    : RECO_META["à_évaluer"];
  const RecoIcon = reco.Icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="termination-sim-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Calculator className="h-3.5 w-3.5 text-white" />
            </div>
            Si je résilie aujourd'hui ?
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          <div className="text-xs text-slate-500 mt-0.5">
            Simulation du coût de résiliation (préavis + indemnité + reste à courir)
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs">Date de la demande de résiliation</Label>
              <div className="mt-1">
                <DatePickerFR value={cancelDate} onChange={setCancelDate} />
              </div>
            </div>
            <Button onClick={() => simulate(cancelDate)} disabled={busy || !cancelDate}
                    className="bg-indigo-600 hover:bg-indigo-700 h-10"
                    data-testid="termination-sim-run">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    : <Calculator className="h-4 w-4 mr-1.5" />}
              Simuler
            </Button>
          </div>

          {sim && (
            <>
              {/* Recommandation */}
              <div className={cn("rounded-lg border-2 p-3 flex items-center gap-3", reco.cls)}
                   data-testid="termination-sim-reco">
                <RecoIcon className="h-6 w-6 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{reco.label}</div>
                  {sim.savings_ht != null && (
                    <div className="text-xs mt-0.5">
                      {sim.savings_ht > 0
                        ? <>Économie potentielle : <b>{fmtEUR(sim.savings_ht)}</b> HT vs aller au bout du terme</>
                        : sim.savings_ht < 0
                        ? <>Surcoût : <b>{fmtEUR(-sim.savings_ht)}</b> HT vs aller au bout du terme</>
                        : <>Coût identique à aller au bout du terme</>}
                    </div>
                  )}
                  {sim.notice_end_before_contract_end === false && (
                    <div className="text-xs mt-0.5">
                      Le préavis expire ({fmtDate(sim.notice_end_date)}) après la fin naturelle
                      du contrat ({fmtDate(sim.contract_end_date)}). Résiliation inutile.
                    </div>
                  )}
                </div>
              </div>

              {/* Chronologie visuelle */}
              <Card className="p-4">
                <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-3">
                  Chronologie
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <TimelineStep icon={Calendar} label="Demande"
                                 date={sim.cancellation_date} tone="blue" />
                  <TimelineStep icon={Calendar} label={`+ ${sim.notice_period_days} j de préavis`}
                                 date={sim.notice_end_date} tone="indigo" active />
                  <TimelineStep icon={Calendar} label="Fin naturelle"
                                 date={sim.contract_end_date} tone={sim.notice_end_before_contract_end === false ? "red" : "slate"} />
                </div>
              </Card>

              {/* Détail chiffré */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="p-4 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                    Si résiliation
                  </div>
                  <Line label="Montant mensuel HT équivalent" value={fmtEUR(sim.monthly_ht)} />
                  <Line label={`Coût préavis (${sim.notice_period_days}j)`} value={fmtEUR(sim.notice_cost_ht)} />
                  {sim.penalty_ht > 0 && (
                    <Line label={`Indemnité${sim.penalty_pct_used ? ` (${sim.penalty_pct_used}%)` : ''}`}
                          value={fmtEUR(sim.penalty_ht)} />
                  )}
                  <div className="border-t border-slate-200 mt-2 pt-2">
                    <Line label="Total à payer" value={fmtEUR(sim.total_termination_cost_ht)}
                          strong tone="indigo" />
                  </div>
                </Card>
                <Card className="p-4 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                    Si on va au bout
                  </div>
                  {sim.contract_end_date ? (
                    <>
                      <Line label="Reste à courir" value={`${sim.remaining_months_at_end} mois`} />
                      <Line label="Fin de contrat" value={fmtDate(sim.contract_end_date)} />
                      <div className="border-t border-slate-200 mt-2 pt-2">
                        <Line label="Coût total HT" value={fmtEUR(sim.cost_until_end_ht)}
                              strong tone="slate" />
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-slate-500 italic mt-2">
                      Aucune date de fin — contrat à durée indéterminée.
                    </div>
                  )}
                </Card>
              </div>

              {/* Note info */}
              <div className="text-[11px] text-slate-500 flex items-start gap-2">
                <Info className="h-3 w-3 shrink-0 mt-0.5" />
                <span>
                  Calcul indicatif basé sur les données du contrat. L'indemnité
                  contractuelle exacte peut varier — vérifie la clause de résiliation
                  anticipée avant d'agir.
                </span>
              </div>
            </>
          )}

          {!sim && !busy && (
            <div className="text-sm text-slate-500 py-6 text-center">
              Choisis une date puis clique sur « Simuler ».
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="termination-sim-close">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TimelineStep({ icon: Icon, label, date, tone = "slate", active = false }) {
  const cls = {
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    indigo: "bg-indigo-100 text-indigo-700 border-indigo-300",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
    red: "bg-red-100 text-red-700 border-red-200",
  }[tone];
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={cn("w-10 h-10 rounded-full flex items-center justify-center border-2",
                          cls, active && "ring-2 ring-indigo-300 ring-offset-2")}>
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <div className="text-[10px] font-medium text-slate-700 uppercase tracking-wide">{label}</div>
      <div className="text-[11px] text-slate-500 tabular-nums">{fmtDate(date)}</div>
    </div>
  );
}

function Line({ label, value, strong = false, tone = "slate" }) {
  const cls = {
    slate: strong ? "text-slate-900" : "text-slate-700",
    indigo: "text-indigo-700",
  }[tone];
  return (
    <div className={cn("flex items-center justify-between text-xs py-0.5",
                        strong && "text-sm")}>
      <span className="text-slate-500">{label}</span>
      <span className={cn("tabular-nums font-mono", strong && "font-semibold", cls)}>{value}</span>
    </div>
  );
}
